import { firestore } from "../firebase";
import { collection, getDocs, getDoc, runTransaction, deleteDoc, query, where, doc as fDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore/lite';
import { FileType, IFile } from "../../store/files/types";
import { union } from "lodash";
import {
    IAddFileAsyncRequestPayload,
    IDeleteFileAsyncRequestPayload, IMoveFileAsyncRequestPayload,
    IRenameFileAsyncRequestPayload
} from "../../store/files/actions";
import { startsWithEndCode } from "../utils";
import { arrayToObject, IdObjectsWithId } from "../../utils";
import { fetchDocById } from "../api";

export const fileSystemCollectionKey = "files";

export async function fetchRoot(collectionKey: string): Promise<IFile> {
    const q = query(collection(firestore, collectionKey), where("path", "==", "/"));
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
        return Promise.reject("No root found!")
    }
    if (snapshot.size > 1) {
        return Promise.reject("Multiple root found!")
    }
    const docs = snapshot.docs.map(doc => doc.data());
    return <IFile>docs[0];
}

export async function fetchRootWithChildren(collectionKey: string): Promise<IdObjectsWithId<IFile>> {
    const root = await fetchRoot(collectionKey)
    const files = await fetchChildren(collectionKey, root)
    files.byId[root.id] = root
    return files
}

export async function fetchChildren(collectionKey: string, file: IFile): Promise<IdObjectsWithId<IFile>> {
    const q = query(collection(firestore, collectionKey), where("parentId", "==", file.id));
    const snapshot = await getDocs(q);
    const docs = snapshot.docs.map(doc => doc.data());
    return arrayToObject(<IFile[]>docs)
}

export async function renameFile(collectionKey: string, params: IRenameFileAsyncRequestPayload): Promise<IdObjectsWithId<IFile>> {
    let { user, name, file } = params;
    if (!(user && user.email)) {
        return Promise.reject(`User is empty.`)
    }
    if (!(file && file.id)) {
        return Promise.reject(`File id is empty.`)
    }
    if (file.name === name) {
        return arrayToObject([])
    }
    const email = user.email;

    let fileRef = fDoc(collection(firestore, collectionKey), file.id);
    try {
        let x = await runTransaction(firestore, async transaction => {
            const doc = await transaction.get(fileRef);
            if (!doc.exists) {
                throw `${file.type} ${file.name} does not exists!`;
            }

            const newPath = file.parentPath === '/'
                ? `${file.parentPath}${name}`
                : `${file.parentPath}/${name}`;


            const q = query(collection(firestore, collectionKey), where("path", "==", newPath));
            const existing = await getDocs(q);
            if (!existing.empty) {
                throw `${newPath} exists!`;
            }


            const successorsRef = query(collection(firestore, collectionKey),
                where("path", ">=", file.path),
                where("path", "<", startsWithEndCode(file.path)));

            let successorsSnapshot = await getDocs(successorsRef);
            successorsSnapshot.docs.map(doc => {
                const successor = doc.data() as IFile
                let successorRef = fDoc(collection(firestore, collectionKey), successor.id);
                transaction.update(successorRef, {
                    path: successor.path.replace(file.path, newPath),
                    parentPath: successor.parentPath ? successor.parentPath.replace(file.path, newPath) : successor.parentPath,
                    updatedBy: email,
                    updated: serverTimestamp(),
                })
            });

            transaction.update(fileRef, {
                name: name,
                path: newPath,
                updatedBy: email,
                updated: serverTimestamp(),
            });

        })
    } catch (e: any) {
        console.log(e);
        return Promise.reject("Adding doc transaction failed." + e.toString())
    }

    const updated: IFile[] = [];
    const updatedDoc = await getDoc(fileRef);
    if (updatedDoc.exists()) {
        const data = <IFile>updatedDoc.data()
        updated.push(data)

        const updatedSuccessorsRef = query(collection(firestore, collectionKey),
            where("path", ">=", data.path),
            where("path", "<", startsWithEndCode(data.path)));
        let successorsSnapshot = await getDocs(updatedSuccessorsRef);
        const docs = successorsSnapshot.docs.map(doc => updated.push(<IFile>doc.data()));
    }
    return arrayToObject(<IFile[]>updated)
}

export async function moveFile(collectionKey: string, params: IMoveFileAsyncRequestPayload): Promise<IdObjectsWithId<IFile>> {
    let { user, parentPath, file } = params;
    if (!(user && user.email)) {
        return Promise.reject(`User is empty.`)
    }
    if (!(file && file.id)) {
        return Promise.reject(`File id is empty.`)
    }
    if (file.parentPath === parentPath) {
        return arrayToObject([])
    }
    const email = user.email;

    let fileRef = fDoc(collection(firestore, collectionKey), file.id);
    try {
        let x = await runTransaction(firestore, async transaction => {
            const doc = await transaction.get(fileRef);
            if (!doc.exists) {
                throw `${file.type} ${file.name} does not exists!`;
            }

            if (!file.parentPath) {
                throw `current parent path ${file.parentPath} is empty!`;
            }

            if (!file.parentId) {
                throw `current parentId ${file.parentId} is empty!`;
            }

            const newPath = parentPath === '/'
                ? `${parentPath}${file.name}`
                : `${parentPath}/${file.name}`;

            const q = query(collection(firestore, collectionKey), where("path", "==", newPath));
            const existing = await getDocs(q);
            if (!existing.empty) {
                throw `${newPath} exists!`;
            }

            // remove from current parent's children
            let currentParentRef = fDoc(collection(firestore, collectionKey), file.parentId);
            const currentParentSnapshot = await getDoc(currentParentRef);

            if (!currentParentSnapshot.exists()) {
                throw "No parent found!"
            }
            const currentParent = <IFile>currentParentSnapshot.data();
            let currentChildren = currentParent.children;
            if (!currentChildren || !currentChildren.includes(file.id)) {
                throw `Current parent ${JSON.stringify(currentParent)} doesn't have such children ${file.id}`
            }

            currentChildren = currentChildren.filter(id => {
                return id != file.id
            })

            transaction.update(currentParentRef, {
                children: currentChildren,
                updatedBy: email,
                updated: serverTimestamp(),
            });

            // add to new parent's children
            const newParentQ = query(collection(firestore, collectionKey), where("path", "==", parentPath));
            const newParentRef = await getDocs(newParentQ);
            if (newParentRef.empty) {
                throw `${parentPath} doesn't exists!`;
            }

            if (newParentRef.size > 1) {
                throw `Multiple ${parentPath} found!`;
            }

            let newParent = <IFile>newParentRef.docs[0].data()

            let newParentDocRef = fDoc(collection(firestore, collectionKey), newParent.id);
            const newParentDoc = await getDoc(newParentDocRef);

            let newChildren = newParent.children;
            if (!newChildren) {
                newChildren = []
            }
            if (!newChildren.includes(file.id)) {
                newChildren.push(file.id)
            }

            transaction.update(newParentDocRef, {
                children: newChildren,
                updatedBy: email,
                updated: serverTimestamp(),
            });


            // update the parent id to new parent

            const successorsRef = query(collection(firestore, collectionKey),
                where("path", ">=", file.path),
                where("path", "<", startsWithEndCode(file.path)));

            let successorsSnapshot = await getDocs(successorsRef);

            successorsSnapshot.docs.map(doc => {
                const successor = doc.data() as IFile
                let successorRef = fDoc(collection(firestore, collectionKey), successor.id);
                transaction.update(successorRef, {
                    path: successor.path.replace(file.path, newPath),
                    parentPath: successor.parentPath ? successor.parentPath.replace(file.path, newPath) : successor.parentPath,
                    updatedBy: email,
                    updated: serverTimestamp(),
                })
            });

            transaction.update(fileRef, {
                parentId: newParent.id,
                path: newPath,
                parentPath: parentPath,
                updatedBy: email,
                updated: serverTimestamp(),
            });

        })
    } catch (e: any) {
        console.log(e);
        return Promise.reject("Moving file transaction failed." + e.toString())
    }

    const updated: IFile[] = [];
    const updatedDoc = await getDoc(fileRef);

    if (updatedDoc.exists()) {
        const data = <IFile>updatedDoc.data()
        updated.push(data)

        let updatedParent = await getDoc(fDoc(collection(firestore, collectionKey), data.parentId));

        if (!updatedParent.exists()) {
            throw "No parent found after update!"
        }

        const parentData = <IFile>updatedParent.data()
        updated.push(parentData)

        const updatedSuccessorsRef = query(collection(firestore, collectionKey),
            where("path", ">=", data.path),
            where("path", "<", startsWithEndCode(data.path)));

        let successorsSnapshot = await getDocs(updatedSuccessorsRef);
        const docs = successorsSnapshot.docs.map(doc => updated.push(<IFile>doc.data()));
    }
    return arrayToObject(<IFile[]>updated)
}

export async function saveFileToCollection(fileSystemCollectionKey: string, fileDataCollectionKey: string, params: IAddFileAsyncRequestPayload): Promise<IFile> {
    let { newFileInfo, parent, isRoot, user } = params;
    if (!(user && user.email)) {
        return Promise.reject(`User is empty.`)
    }
    const email = user.email;

    let file: IFile = {} as IFile;
    file.createdBy = email;
    file.updatedBy = email;
    if (isRoot) {
        file.type = FileType.FOLDER;
        file.name = '/';
        file.path = '/';
    } else if (parent && parent.id && parent.path) {
        if (!newFileInfo.name && !newFileInfo.type) {
            return Promise.reject("Filename and type can't be empty")
        }
        file.name = newFileInfo.name;
        file.type = newFileInfo.type;
        file.parentPath = parent.path;
        file.parentId = parent.id;
        file.path = file.parentPath === '/'
            ? `${file.parentPath}${file.name}`
            : `${file.parentPath}/${file.name}`
    } else {
        return Promise.reject("Parent is empty")
    }
    if (file.type === FileType.FOLDER) {
        file.children = []
    }

    let newFileRef = fDoc(collection(firestore, fileSystemCollectionKey));
    file.id = newFileRef.id;
    try {
        await runTransaction(firestore, async transaction => {

            const existingQ = query(collection(firestore, fileSystemCollectionKey), where("path", "==", file.path));
            const existing = await getDocs(existingQ);

            if (!existing.empty) {
                throw `${file.type} ${file.name} exists!`;
            }

            let parent: IFile = {} as IFile;

            if (!isRoot) {
                let parentRef = fDoc(collection(firestore, fileSystemCollectionKey), file.parentId);
                let parentDoc = await transaction.get(parentRef);
                if (!parentDoc.exists()) {
                    throw "Parent Document does not exist!";
                }
                parent = parentDoc.data() as IFile;
                parent.children = union(parent.children, [file.id]);
                transaction.update(parentDoc.ref, {
                    children: parent.children,
                    updatedBy: email,
                    updated: serverTimestamp(),
                });
            }

            if (file.type === FileType.FILE) {
                let newFileDataRef = fDoc(collection(firestore, fileDataCollectionKey));
                transaction.set(newFileDataRef, {
                    id: newFileDataRef.id,
                    data: '',
                    createdBy: email,
                    created: serverTimestamp(),
                    updated: serverTimestamp(),
                })

                file.fileDataId = newFileDataRef.id
            }

            await transaction.set(newFileRef, {
                ...file,
                createdBy: email,
                created: serverTimestamp(),
                updated: serverTimestamp(),
            });
        })
    } catch (e: any) {
        console.log(e);
        return Promise.reject("Adding doc transaction failed." + e.toString())
    }

    return fetchDocById(fileSystemCollectionKey, newFileRef.id)
}

export async function deleteFileFromCollection(fileSystemCollectionKey: string, fileDataCollectionKey: string, params: IDeleteFileAsyncRequestPayload): Promise<any> {
    let { file, user } = params;
    if (!(user && user.email)) {
        return Promise.reject(`User is empty.`)
    }
    const email = user.email;

    let fileRef = fDoc(collection(firestore, fileSystemCollectionKey), file.id);
    try {
        return await runTransaction(firestore, async transaction => {
            const doc = await transaction.get(fileRef);
            if (!doc.exists) {
                throw `${file.type} ${file.name} does not exists!`;
            }


            // delete from parent children
            if (file.parentId) {
                let parentRef = fDoc(collection(firestore, fileSystemCollectionKey), file.parentId);
                let parentDoc = await transaction.get(parentRef);
                if (!parentDoc.exists()) {
                    throw "Parent Document does not exist!";
                }
                let parent = parentDoc.data() as IFile;
                if (parent.children) {
                    let index = parent.children.indexOf(file.id)
                    if (index != -1) {
                        parent.children.splice(index, 1)
                    }
                    parent.children = union(parent.children, [file.id]);
                    transaction.update(parentDoc.ref, {
                        children: parent.children,
                        updatedBy: email,
                        updated: serverTimestamp(),
                    });
                }
            }

            const successorsRef = query(collection(firestore, fileSystemCollectionKey),
                where("path", ">=", file.path),
                where("path", "<", startsWithEndCode(file.path)));

            let successorsSnapshot = await getDocs(successorsRef);
            successorsSnapshot.docs.map(doc => {
                const file = doc.data() as IFile
                let successorRef = fDoc(collection(firestore, fileSystemCollectionKey), file.id);
                if (file.type === FileType.FILE && file.fileDataId) {
                    let fileDataRef = fDoc(collection(firestore, fileDataCollectionKey), file.fileDataId);
                    transaction.delete(fileDataRef)
                }
                transaction.delete(successorRef)
            });
            if (file.type === FileType.FILE && file.fileDataId) {
                let fileDataRef = fDoc(collection(firestore, fileDataCollectionKey), file.fileDataId);
                transaction.delete(fileDataRef)
            }
            transaction.delete(fileRef)
            return <IFile>doc.data()
        })
    } catch (e: any) {
        console.log(e);
        return Promise.reject("Adding doc transaction failed." + e.toString())
    }
}
